import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'

interface HeroSectionProps {
  title: string
  subtitle?: string
  backgroundImage?: string
  backgroundVideo?: string
  variant: 'full' | 'compact'
  cta?: {
    text: string
    link: string
  }
  secondaryCta?: {
    text: string
    link: string
    external?: boolean
  }
  overlay?: 'none' | 'dark' | 'brand'
  showBreadcrumb?: boolean
  backgroundSize?: string
  backgroundPosition?: string
}

export function HeroSection({
  title,
  subtitle,
  backgroundImage,
  backgroundVideo,
  variant,
  cta,
  secondaryCta,
  overlay = 'dark',
  showBreadcrumb = false,
  backgroundSize,
  backgroundPosition,
}: HeroSectionProps) {
  const { t, i18n } = useTranslation('common')
  const navigate = useNavigate()

  const isFull = variant === 'full'
  const overlayClass =
    overlay === 'dark'
      ? 'bg-black/70'
      : overlay === 'brand'
        ? 'bg-brand/50'
        : ''

  const scrollToContent = () => {
    const content = document.getElementById('main-content')
    content?.scrollIntoView({ behavior: 'smooth' })
  }

  return (
    <section
      className={cn(
        'relative flex justify-center overflow-hidden bg-neutral-900',
        isFull ? 'h-screen min-h-[600px] items-center' : 'h-[40vh] min-h-[380px] items-end'
      )}
    >
      {/* Background */}
      {backgroundVideo ? (
        <video
          autoPlay
          muted
          loop
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
        >
          <source src={backgroundVideo} type="video/mp4" />
        </video>
      ) : backgroundImage ? (
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url(${backgroundImage})`,
            ...(backgroundSize && { backgroundSize }),
            ...(backgroundPosition && { backgroundPosition }),
          }}
        />
      ) : (
        <div className="absolute inset-0 bg-neutral-800" />
      )}

      {/* Overlay */}
      {overlay !== 'none' && (
        <div className={cn('absolute inset-0', overlayClass)} />
      )}

      {/* Content */}
      <div className={cn(
        'relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center',
        isFull ? '' : 'pt-24 pb-12 md:pt-32 md:pb-16'
      )}>
        {/* Breadcrumb */}
        {showBreadcrumb && !isFull && (
          <nav className="mb-4" aria-label="Breadcrumb">
            <button
              onClick={() => navigate(`/${i18n.language}`)}
              className="text-white/70 hover:text-white text-sm cursor-pointer"
            >
              {t('nav.home')}
            </button>
            <span className="text-white/50 mx-2">/</span>
            <span className="text-white text-sm">{title}</span>
          </nav>
        )}

        <h1
          className={cn(
            'text-white font-heading font-bold drop-shadow-[0_4px_4px_rgba(0,0,0,0.8)]',
            isFull ? 'text-5xl md:text-6xl lg:text-7xl mb-6' : 'text-3xl md:text-4xl lg:text-5xl mb-4'
          )}
        >
          {title}
        </h1>

        {subtitle && (
          <p
            className={cn(
              'text-white font-body max-w-3xl mx-auto drop-shadow-[0_2px_2px_rgba(0,0,0,0.8)]',
              isFull ? 'text-xl md:text-2xl mb-8' : 'text-lg md:text-xl mb-6'
            )}
          >
            {subtitle}
          </p>
        )}

        {(cta || secondaryCta) && (
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            {cta && (
              <button
                onClick={() => navigate(cta.link)}
                className={cn(
                  'inline-flex items-center justify-center px-8 py-4 font-heading font-semibold rounded-md transition-all duration-fast',
                  'bg-brand text-neutral-900 hover:bg-brand-dark hover:text-neutral-900 hover:scale-105 shadow-lg cursor-pointer'
                )}
              >
                {cta.text}
              </button>
            )}
            {secondaryCta && (
              secondaryCta.external ? (
                <a
                  href={secondaryCta.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={cn(
                    'inline-flex items-center justify-center px-8 py-4 font-heading font-semibold rounded-md transition-all duration-fast',
                    'bg-white/10 text-white border border-white/30 hover:bg-white/20 hover:scale-105 shadow-lg backdrop-blur-sm'
                  )}
                >
                  {secondaryCta.text}
                </a>
              ) : (
                <button
                  onClick={() => navigate(secondaryCta.link)}
                  className={cn(
                    'inline-flex items-center justify-center px-8 py-4 font-heading font-semibold rounded-md transition-all duration-fast',
                    'bg-white/10 text-white border border-white/30 hover:bg-white/20 hover:scale-105 shadow-lg backdrop-blur-sm cursor-pointer'
                  )}
                >
                  {secondaryCta.text}
                </button>
              )
            )}
          </div>
        )}
      </div>

      {/* Scroll Indicator (Full variant only) */}
      {isFull && (
        <button
          onClick={scrollToContent}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/70 hover:text-white transition-colors animate-bounce"
          aria-label={t('aria.scrollDown')}
        >
          <ChevronDown className="w-10 h-10" />
        </button>
      )}

      {/* Bottom Gradient */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-black/30 to-transparent pointer-events-none" />
    </section>
  )
}
